import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { ChatApi, MemberInfo } from 'api';

export type RoomInfo = {
  id: string;
  type: string;
  displayName: string;
  avatar: string;
  entityID: string;
  unreadMessages: number;
  createdAt: string;
  updatedAt: string;
};

export type Message = {
  id: string;
  roomID: string;
  senderID: string;
  room: any;
  sender: any;
  content: string;
  messageType: string;
  createdAt: string;
  updatedAt: string | number;
};

export const createRoom = createAsyncThunk(
  'chat/createRoom',
  async (
    {
      type,
      entityID,
      members,
      displayName,
      avatar,
    }: {
      type: string;
      entityID: string;
      members: MemberInfo[];
      displayName?: string;
      avatar?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await ChatApi.createRoom(
        type,
        entityID,
        members,
        displayName,
        avatar
      );
      return response.data as any;
    } catch (err: any) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue({ message: err.response.data });
    }
  }
);

export const addMember = createAsyncThunk(
  'chat/addMember',
  async (
    {
      roomID,
      newMemberID,
      role,
    }: { roomID: string; newMemberID: string; role: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await ChatApi.addMember(roomID, newMemberID, role);
      return response;
    } catch (err: any) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue({ message: err.response.data });
    }
  }
);

export const addMembers = createAsyncThunk(
  'chat/addMembers',
  async (
    { roomID, members }: { roomID: string; members: MemberInfo[] },
    { rejectWithValue }
  ) => {
    try {
      const response = await ChatApi.addMembers(roomID, members);
      return response;
    } catch (err: any) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue({ message: err.response.data });
    }
  }
);

export const getMembers = createAsyncThunk(
  'chat/getMembers',
  async ({ roomID }: { roomID: string }, { rejectWithValue }) => {
    try {
      const response = await ChatApi.getMembers(roomID);
      return response.data;
    } catch (err: any) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue({ message: err.response.data });
    }
  }
);

export const findRooms = createAsyncThunk(
  'chat/findRooms',
  async (
    { entityID, roomType }: { entityID: string; roomType?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await ChatApi.findRooms(entityID, roomType);
      return response.data as any[];
    } catch (err: any) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue({ message: err.response.data });
    }
  }
);

export const findMessages = createAsyncThunk(
  'chat/findMessages',
  async (
    {
      roomID,
      memberID,
      page,
      take,
    }: { roomID: string; memberID: string; page: number; take: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await ChatApi.findMessages(roomID, memberID, page, take);
      return response.data as any[];
    } catch (err: any) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue({ message: err.response.data });
    }
  }
);

export const findMessagesWithType = createAsyncThunk(
  'chat/findMessagesWithType',
  async (
    {
      roomID,
      memberID,
      page,
      take,
      messageType,
    }: {
      roomID: string;
      memberID: string;
      page: number;
      take: number;
      messageType: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await ChatApi.findMessages(
        roomID,
        memberID,
        page,
        take,
        messageType
      );
      return response.data as any[];
    } catch (err: any) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue({ message: err.response.data });
    }
  }
);

export const sendMessage = createAsyncThunk(
  'chat/sendMessage',
  async (
    {
      roomID,
      memberID,
      content,
      messageType,
    }: {
      roomID: string;
      memberID: string;
      content: string;
      messageType: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await ChatApi.sendMessage(
        roomID,
        memberID,
        content,
        messageType
      );
      return response.data as unknown as Message;
    } catch (err: any) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue({ message: err.response.data });
    }
  }
);

export const readAllMessages = createAsyncThunk(
  'chat/readAllMessages',
  async (
    { roomID, memberID }: { roomID: string; memberID: string },
    { rejectWithValue }
  ) => {
    try {
      await ChatApi.readAllMessages(roomID, memberID);
      return { roomID };
    } catch (err: any) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue({ message: err.response.data });
    }
  }
);

interface IState {
  visibleTool: boolean;
  rooms: any[];
}

const initialState: IState = {
  visibleTool: false,
  rooms: [],
};

export const sortChatRoomsFunc = (a, b) => {
  const time1: number =
    a.messages && a.messages.length
      ? new Date(a.messages[a.messages.length - 1].updatedAt).getTime()
      : new Date(a.updatedAt).getTime();
  const time2: number =
    b.messages && b.messages.length
      ? new Date(b.messages[b.messages.length - 1].updatedAt).getTime()
      : new Date(b.updatedAt).getTime();
  return time2 - time1;
};

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    changeVisible(state) {
      const temp = state.visibleTool;
      state.visibleTool = !temp;
    },
    updateRoom(state, { payload }) {
      const { data } = payload;
      const roomList = state.rooms.slice();
      const idx = roomList.findIndex(room => room.id === data.roomID);
      if (idx > -1) {
        const isGroupChat = data.room.type === 'group';
        const avatar = isGroupChat ? data.room.avatar : data.sender.avatar;
        const displayName = isGroupChat
          ? data.room.displayName
          : data.sender.displayName;
        if (!roomList[idx].messages) {
          roomList[idx].messages = [];
        }
        roomList[idx].messages.push(data);
        roomList[idx] = {
          ...data.room,
          avatar,
          displayName,
          unreadMessages: roomList[idx].unreadMessages + 1,
          messages: roomList[idx].messages,
        };
        roomList.sort(sortChatRoomsFunc);
        state.rooms = roomList;
      } else {
        const { room, ...newMessage } = data;
        const newRoom = room;
        newRoom.avatar = newMessage.sender?.avatar;
        newRoom.displayName = newMessage.sender?.displayName;
        newRoom.unreadMessages = 1;
        newRoom.messages = [newMessage];
        state.rooms.unshift(newRoom);
      }
    },
  },
  extraReducers: builder => {
    builder.addCase(findRooms.fulfilled, (state, { payload }) => {
      state.rooms = payload;
    });
    builder.addCase(createRoom.fulfilled, (state, { payload }) => {
      state.rooms.unshift(payload);
    });
    builder.addCase(findMessages.fulfilled, (state, { payload }) => {
      const roomList = state.rooms.slice();
      if (payload.length) {
        const { roomID } = payload[0];
        const roomToBeUpdated = roomList.find(room => room.id === roomID);
        if (roomToBeUpdated) {
          roomToBeUpdated.messages = payload.reverse();
        }
        roomList.sort(sortChatRoomsFunc);
        state.rooms = roomList;
      }
    });
    builder.addCase(sendMessage.fulfilled, (state, { payload }) => {
      const { roomID } = payload;
      const roomToBeUpdated = state.rooms.find(room => room.id === roomID);
      if (roomToBeUpdated) {
        if (!roomToBeUpdated.messages) {
          roomToBeUpdated.messages = [];
        }
        if (!payload.updatedAt) {
          payload.updatedAt = Date.now();
        }
        roomToBeUpdated.messages.push(payload);
        state.rooms.sort(sortChatRoomsFunc);
      }
    });
    builder.addCase(readAllMessages.fulfilled, (state, { payload }) => {
      const { roomID } = payload;
      const updatedRoom = state.rooms.find(room => room.id === roomID);
      if (updatedRoom) {
        updatedRoom.unreadMessages = 0;
      }
    });
  },
});

const { actions, reducer: chatReducer } = chatSlice;

export const { changeVisible, updateRoom } = actions;
export default chatReducer;
